import { Badge, Button, Card, LoadingContainer, Modal } from '@/components/ui';
import { useAudiosSysadmin, useEmpresasSysadmin } from '@/hooks/useSysadmin';
import type { AudioSysadminDTO } from '@/services/sysadmin.service';
import { AlertTriangle, ExternalLink, FileWarning, FolderSearch, Layers, Mic, Pencil, Share2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { AsesoriasHorasPorRechazoPanel } from './AsesoriasHorasPorRechazoPanel';
import { AudiosEliminadosReasignacionPanel } from './AudiosEliminadosReasignacionPanel';
import { AudiosDuplicadosPanel } from './AudiosDuplicadosPanel';
import { AuditoriaNombresAudiosPanel } from './AuditoriaNombresAudiosPanel';
import { GestionDriveAudiosPanel } from './GestionDriveAudiosPanel';

export function GestionAudiosPage() {
  const [activeTab, setActiveTab] = useState<'bd' | 'drive' | 'reasignados' | 'horas-rechazo' | 'duplicados' | 'nombres'>('bd');
  const { audios, isLoading, error, eliminarAudio, actualizarAudio, compartirAudio } = useAudiosSysadmin();
  const { empresas, isLoading: loadingEmpresas } = useEmpresasSysadmin();
  const [audioToDelete, setAudioToDelete] = useState<AudioSysadminDTO | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [audioToEdit, setAudioToEdit] = useState<AudioSysadminDTO | null>(null);
  const [editDurationStr, setEditDurationStr] = useState('');
  const [editAudioData, setEditAudioData] = useState<Partial<AudioSysadminDTO>>({});
  const [isEditing, setIsEditing] = useState(false);

  const [audioToShare, setAudioToShare] = useState<AudioSysadminDTO | null>(null);
  const [shareEmails, setShareEmails] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [shareResult, setShareResult] = useState<{ exitosos: string[]; fallidos: string[] } | null>(null);

  const handleDeleteConfirm = async () => {
    if (!audioToDelete) return;
    setIsDeleting(true);
    try {
      await eliminarAudio(audioToDelete.audioId);
      alert('Audio eliminado correctamente');
      setAudioToDelete(null);
    } catch (err: any) {
      alert(err.message || 'Error al eliminar el audio');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const min = Math.floor((seconds % 3600) / 60);
    const sec = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const getDriveFileUrl = (adjunteDelAudio: string | null | undefined) => {
    if (!adjunteDelAudio) return null;
    const value = adjunteDelAudio.trim();
    if (!value) return null;

    // Si ya es una URL de Drive, la usamos tal cual.
    if (value.includes('drive.google.com')) return value;

    // Intenta extraer un ID de archivo de Drive y generar la URL estándar.
    const match = value.match(/([a-zA-Z0-9_-]{25,})/);
    if (match) return `https://drive.google.com/file/d/${match[1]}/view`;

    return null;
  };

  const handleOpenDriveUrl = (audio: AudioSysadminDTO) => {
    const url = getDriveFileUrl(audio.adjunteDelAudio);
    if (!url) {
      alert('No hay una URL válida de Google Drive disponible para este audio.');
      return;
    }
    window.open(url, '_blank');
  };

  const handleOpenEdit = (audio: AudioSysadminDTO) => {
    setAudioToEdit(audio);
    setEditDurationStr(formatDuration(audio.duracionSeg));

    // Convertir de UTC a string local compatible con datetime-local (YYYY-MM-DDTHH:mm:SS)
    let localFecha = '';
    if (audio.fechaGrabacion) {
      const d = new Date(audio.fechaGrabacion);
      // d.getTimezoneOffset() devuelve minutos. Lo pasamos a ms.
      const offsetMs = d.getTimezoneOffset() * 60000;
      localFecha = new Date(d.getTime() - offsetMs).toISOString().slice(0, 19);
    }

    setEditAudioData({
      adjunteDelAudio: audio.adjunteDelAudio || '',
      email: audio.email || '',
      fechaGrabacion: localFecha,
      asesorId: audio.asesorId,
    });
  };

  const handleEditConfirm = async () => {
    if (!audioToEdit) return;

    let newDurationSeg = audioToEdit.duracionSeg;
    if (editDurationStr) {
      const parts = editDurationStr.split(':');
      if (parts.length === 3) {
        newDurationSeg = parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
      } else if (parts.length === 2) {
        newDurationSeg = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
      } else if (parts.length === 1) {
        newDurationSeg = parseInt(parts[0], 10);
        if (isNaN(newDurationSeg)) newDurationSeg = 0;
      }

      if (isNaN(newDurationSeg) || newDurationSeg < 0) {
        alert('Formato de duración inválido. Use HH:MM:SS, MM:SS o Segundos.');
        return;
      }
    }

    setIsEditing(true);
    try {
      await actualizarAudio(audioToEdit.audioId, {
        duracionSeg: newDurationSeg,
        adjunteDelAudio: editAudioData.adjunteDelAudio,
        email: editAudioData.email,
        fechaGrabacion: editAudioData.fechaGrabacion ? new Date(editAudioData.fechaGrabacion).toISOString() : undefined,
        asesorId: editAudioData.asesorId ? Number(editAudioData.asesorId) : undefined,
      });
      setAudioToEdit(null);
    } catch (err: any) {
      alert(err.message || 'Error al actualizar el audio');
    } finally {
      setIsEditing(false);
    }
  };

  const handleOpenShare = (audio: AudioSysadminDTO) => {
    setAudioToShare(audio);
    setShareEmails('');
    setShareResult(null);
  };

  const handleShareConfirm = async () => {
    if (!audioToShare || !shareEmails.trim()) return;

    const emailsList = shareEmails.split(/[\s,]+/).filter((e) => e.trim());
    if (emailsList.length === 0) {
      alert('Ingresa al menos un correo válido');
      return;
    }

    setIsSharing(true);
    setShareResult(null);
    try {
      const result = await compartirAudio(audioToShare.audioId, emailsList);
      setShareResult({ exitosos: result.exitosos || [], fallidos: result.fallidos || [] });
    } catch (err: any) {
      alert(err.message || 'Error al compartir audio');
    } finally {
      setIsSharing(false);
    }
  };

  const getEmpresaNombre = (nit: string) => {
    const emp = empresas.find((e) => e.nitId === nit);
    return emp ? emp.nombre : 'Desconocida';
  };

  const isDataLoading = isLoading || loadingEmpresas;

  if (isDataLoading && audios.length === 0) return <LoadingContainer text="Cargando audios..." />;

  if (error) {
    return (
      <Card className="p-4 bg-red-50 border-red-100 flex items-center gap-3 text-red-700">
        <p className="text-sm font-medium">Error al cargar audios: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Mic className="w-5 h-5 text-blue-600" />
            Gestión de Grabaciones y Audios
          </h2>
          <p className="text-sm text-gray-500 mt-1">Explora, administra, normaliza y reproduce grabaciones del sistema y Google Drive.</p>
        </div>
        <Badge variant="blue">
          {activeTab === 'bd'
            ? `${audios.length} Registros en BD`
            : activeTab === 'drive'
              ? 'Google Drive'
              : activeTab === 'reasignados'
                ? 'Reasignaciones'
                : activeTab === 'horas-rechazo'
                  ? 'Horas por rechazo'
                  : activeTab === 'nombres'
                    ? 'Nombres de audios'
                    : 'Audios Duplicados'}
        </Badge>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 gap-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('bd')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'bd'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Mic className="w-4.5 h-4.5" />
          Grabaciones en Base de Datos ({audios.length})
        </button>

        <button
          onClick={() => setActiveTab('duplicados')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'duplicados'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Layers className="w-4.5 h-4.5" />
          Audios Duplicados / Repetidos (Auditoría & Excel)
        </button>

        <button
          onClick={() => setActiveTab('nombres')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'nombres'
              ? 'border-rose-600 text-rose-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FileWarning className="w-4.5 h-4.5" />
          Nombres de Audios (Formato)
        </button>

        <button
          onClick={() => setActiveTab('drive')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'drive'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FolderSearch className="w-4.5 h-4.5" />
          Explorador de Google Drive (Normalización)
        </button>

        <button
          onClick={() => setActiveTab('reasignados')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'reasignados'
              ? 'border-amber-500 text-amber-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <AlertTriangle className="w-4.5 h-4.5" />
          Eliminados por Reasignación de Asesor
        </button>

        <button
          onClick={() => setActiveTab('horas-rechazo')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'horas-rechazo'
              ? 'border-amber-500 text-amber-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <AlertTriangle className="w-4.5 h-4.5" />
          Horas Infladas por Audios Rechazados
        </button>
      </div>

      {activeTab === 'duplicados' ? (
        <AudiosDuplicadosPanel />
      ) : activeTab === 'nombres' ? (
        <AuditoriaNombresAudiosPanel />
      ) : activeTab === 'drive' ? (
        <GestionDriveAudiosPanel />
      ) : activeTab === 'reasignados' ? (
        <AudiosEliminadosReasignacionPanel />
      ) : activeTab === 'horas-rechazo' ? (
        <AsesoriasHorasPorRechazoPanel />
      ) : (
        <>
          <Card className="overflow-hidden border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">ID Audio</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Asesor</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Empresa (NIT)</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Asesoría</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Duración</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Fecha Grabación</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {audios.length > 0 ? (
                    audios.map((audio: AudioSysadminDTO) => (
                      <tr key={audio.audioId} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-4 py-3 text-gray-900 font-medium">#{audio.audioId}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div>ID: {audio.asesorId}</div>
                          {audio.email && <div className="text-xs text-gray-500">{audio.email}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">
                          {getEmpresaNombre(audio.empresaId)}
                          <div className="text-xs text-gray-500 font-normal mt-0.5">NIT: {audio.empresaId}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">#{audio.asesoriaId}</td>
                        <td className="px-4 py-3 text-gray-600 font-mono">{formatDuration(audio.duracionSeg)}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {new Date(audio.fechaGrabacion).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDriveUrl(audio)}
                              className="hover:text-indigo-700 hover:bg-indigo-100 text-indigo-600"
                              title="Abrir en Drive"
                              disabled={!getDriveFileUrl(audio.adjunteDelAudio)}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenShare(audio)}
                              className="hover:text-green-700 hover:bg-green-100 text-green-600"
                              title="Compartir Audio"
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(audio)}
                              className="hover:text-blue-700 hover:bg-blue-100 text-blue-600"
                              title="Editar Audio"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAudioToDelete(audio)}
                              className="hover:text-red-700 hover:bg-red-100 text-red-600"
                              title="Eliminar Audio"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400 italic">
                        No se encontraron audios registrados en el sistema.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="mt-8 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Vista Simplificada (Audio y Duración)</h3>
          </div>

          <Card className="overflow-hidden border-gray-200 mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">ID Audio</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Duración</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {audios.length > 0 ? (
                    audios.map((audio: AudioSysadminDTO) => {
                      const hasUrl = !!getDriveFileUrl(audio.adjunteDelAudio);
                      return (
                        <tr
                          key={`simple-${audio.audioId}`}
                          className={`transition-colors ${hasUrl ? 'hover:bg-blue-50/50 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                          onClick={() => hasUrl && handleOpenDriveUrl(audio)}
                          title={hasUrl ? 'Abrir en Drive' : 'Sin URL disponible'}
                        >
                          <td className="px-4 py-3 text-blue-600 font-medium hover:underline">{audio.adjunteDelAudio}</td>
                          <td className="px-4 py-3 text-gray-600 font-mono flex items-center gap-2">
                            {formatDuration(audio.duracionSeg)}
                            {hasUrl && <ExternalLink className="w-3 h-3 text-gray-400" />}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-4 py-8 text-center text-gray-400 italic">
                        No se encontraron audios registrados en el sistema.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Modal isOpen={!!audioToDelete} onClose={() => setAudioToDelete(null)} title="Eliminar Audio">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                ¿Estás seguro de que deseas eliminar el audio <strong>#{audioToDelete?.audioId}</strong> de forma permanente?
              </p>
              <div className="p-3 bg-yellow-50 text-yellow-800 text-xs rounded-lg border border-yellow-200">
                <strong>Nota:</strong> Esta acción solo borra el registro de la DB, el archivo original en Google Drive no se elimina aquí.
                Esta acción no se puede deshacer.
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="secondary" onClick={() => setAudioToDelete(null)} disabled={isDeleting}>
                  Cancelar
                </Button>
                <Button variant="danger" onClick={handleDeleteConfirm} isLoading={isDeleting}>
                  Sí, Eliminar
                </Button>
              </div>
            </div>
          </Modal>

          <Modal isOpen={!!audioToEdit} onClose={() => setAudioToEdit(null)} title="Editar Detalles del Audio">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Puedes corregir los datos de auditoría del audio <strong>#{audioToEdit?.audioId}</strong> si no se detectaron correctamente.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Duración (HH:MM:SS)</label>
                  <input
                    type="text"
                    value={editDurationStr}
                    onChange={(e) => setEditDurationStr(e.target.value)}
                    placeholder="Ej: 00:05:30"
                    className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-200 outline-none block text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Correo Electrónico</label>
                  <input
                    type="email"
                    value={editAudioData.email || ''}
                    onChange={(e) => setEditAudioData({ ...editAudioData, email: e.target.value })}
                    placeholder="Ej: usuario@ejemplo.com"
                    className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-200 outline-none block text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Fecha de Grabación</label>
                  <input
                    type="datetime-local"
                    step="1"
                    value={editAudioData.fechaGrabacion || ''}
                    onChange={(e) => setEditAudioData({ ...editAudioData, fechaGrabacion: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-200 outline-none block text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">ID del Asesor</label>
                  <input
                    type="number"
                    value={editAudioData.asesorId || ''}
                    onChange={(e) => setEditAudioData({ ...editAudioData, asesorId: parseInt(e.target.value) || undefined })}
                    className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-200 outline-none block text-sm"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">URL del Audio (Drive)</label>
                  <input
                    type="url"
                    value={editAudioData.adjunteDelAudio || ''}
                    onChange={(e) => setEditAudioData({ ...editAudioData, adjunteDelAudio: e.target.value })}
                    placeholder="https://drive.google.com/..."
                    className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-200 outline-none block text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="secondary" onClick={() => setAudioToEdit(null)} disabled={isEditing}>
                  Cancelar
                </Button>
                <Button onClick={handleEditConfirm} isLoading={isEditing}>
                  Guardar Cambios
                </Button>
              </div>
            </div>
          </Modal>

          <Modal
            isOpen={!!audioToShare}
            onClose={() => {
              setAudioToShare(null);
              setShareResult(null);
            }}
            title="Compartir enlace de Audio"
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Comparte el enlace del audio <strong>#{audioToShare?.audioId}</strong> con otros correos de forma expedita a través de
                Google Drive.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Correos (separados por coma)</label>
                <textarea
                  rows={3}
                  value={shareEmails}
                  onChange={(e) => setShareEmails(e.target.value)}
                  placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
                  className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-200 outline-none block text-sm resize-none"
                />
              </div>

              {shareResult && (
                <div className="space-y-2 text-sm">
                  {shareResult.exitosos.length > 0 && (
                    <div className="p-2 border border-green-200 bg-green-50 text-green-800 rounded">
                      <strong>Enviados exitosamente:</strong>
                      <ul className="list-disc ml-5 mt-1 text-xs">
                        {shareResult.exitosos.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {shareResult.fallidos.length > 0 && (
                    <div className="p-2 border border-red-200 bg-red-50 text-red-800 rounded">
                      <strong>Fallaron al enviar:</strong>
                      <ul className="list-disc ml-5 mt-1 text-xs">
                        {shareResult.fallidos.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAudioToShare(null);
                    setShareResult(null);
                  }}
                  disabled={isSharing}
                >
                  Cerrar
                </Button>
                <Button onClick={handleShareConfirm} isLoading={isSharing}>
                  Compartir Audio
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
